import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { diaChiRiengRepository } from '../../../lib/repositories/dia-chi-rieng.repository';
import { useToast } from '../../../lib/toast';
import type { CustomHost } from '../types';

export const KHOA_DIA_CHI_RIENG = {
  cuaTenant: (tenantId: string) => ['dia-chi-rieng', tenantId] as const,
};

/** Domain thương hiệu của một tenant (ADR-0097). */
export function useDiaChiRieng(tenantId: string) {
  return useQuery({
    queryKey: KHOA_DIA_CHI_RIENG.cuaTenant(tenantId),
    queryFn: () => diaChiRiengRepository.danhSach(tenantId),
  });
}

function useLamMoi(tenantId: string) {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: KHOA_DIA_CHI_RIENG.cuaTenant(tenantId) });
}

/** Thêm domain. Lỗi **không** đi toast: hộp thêm giữ chữ vừa gõ và hiện lỗi dưới ô. */
export function useThemDiaChi(tenantId: string) {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi(tenantId);
  return useMutation({
    mutationFn: ({ hostname, khoaLap }: { hostname: string; khoaLap: string }) =>
      diaChiRiengRepository.them(tenantId, hostname, khoaLap),
    onSuccess: (moi: CustomHost) => {
      lamMoi();
      toast.success(t('diaChiRieng.daThem', { host: moi.hostname }));
    },
  });
}

/**
 * Kiểm DNS ngay. DNS chưa đạt là kết quả bình thường: báo trung tính kèm lý do ở bảng, không tông đỏ. Lỗi thật
 * (404, mạng) không có hộp nào để hiện nên đi toast — `onError` đặt ở nơi gọi.
 */
export function useKiemDnsDiaChi(tenantId: string) {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi(tenantId);
  return useMutation({
    mutationFn: (hostId: string) => diaChiRiengRepository.kiemDns(tenantId, hostId),
    onSuccess: (h: CustomHost) => {
      lamMoi();
      if (h.dns_error) toast.info(t('diaChiRieng.dnsChuaDat', { host: h.hostname }));
      else if (h.status === 'DA_XAC_MINH') toast.success(t('diaChiRieng.dnsDaDat', { host: h.hostname }));
      else toast.info(t('diaChiRieng.dnsVanDat', { host: h.hostname }));
    },
  });
}

/** `khoa`: quản trị nền tảng khoá domain đã tạm ngưng — cùng `suspend`, server chỉ đặt `platform_locked`. */
export type ThaoTacTrangThai = 'kichHoat' | 'tamNgung' | 'khoa' | 'go';

const TOAST_XONG: Record<ThaoTacTrangThai, KhoaDich> = {
  kichHoat: 'diaChiRieng.daKichHoat',
  tamNgung: 'diaChiRieng.daTamNgung',
  khoa: 'diaChiRieng.daKhoa',
  go: 'diaChiRieng.daGo',
};

/** Kích hoạt, tạm ngưng, khoá, gỡ: đi qua hộp xác nhận, lỗi hiện trong hộp. */
export function useDoiTrangThaiDiaChi(tenantId: string) {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi(tenantId);
  return useMutation({
    mutationFn: async ({ loai, host }: { loai: ThaoTacTrangThai; host: CustomHost }) => {
      if (loai === 'kichHoat') await diaChiRiengRepository.kichHoat(tenantId, host.id);
      else if (loai === 'tamNgung' || loai === 'khoa') await diaChiRiengRepository.tamNgung(tenantId, host.id);
      else await diaChiRiengRepository.go(tenantId, host.id);
    },
    onSuccess: (_kq, { loai, host }) => {
      lamMoi();
      toast.success(t(TOAST_XONG[loai], { host: host.hostname }));
    },
  });
}
